"use client";

import React, { useState } from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Loader as Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
export default function EstimateRequestForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType, setProjectType] = useState("");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    setZip(val);
    if (val && !/^\d{5}$/.test(val)) {
      setZipError("Please enter a valid 5-digit ZIP code.");
    } else {
      setZipError("");
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let hasError = false;
    if (!/^\d{5}$/.test(zip)) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      hasError = true;
    } else {
      setZipError("");
    }
    if (!consent) {
      setConsentError(true);
      hasError = true;
    } else {
      setConsentError(false);
    }
    if (!projectType) {
      hasError = true;
    }
    if (hasError) {
      return;
    }
    setStatus("submitting");
    setErrorMessage("");
    try {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const body = new URLSearchParams({
        name,
        phone,
        email,
        project_type: projectType,
        address: zip,
        zip,
        details: message,
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: pageUrl,
        form_location: "process-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body
      });
      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setStatus("error");
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="rounded-2xl border border-border bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10 transition-all">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold tracking-tight text-foreground">
              Request an estimate
            </h2>
            <p className="mt-3 text-base text-muted-foreground max-w-xl mx-auto">
              Share details about your upcoming project in Brookhaven or nearby communities, and our team will get back to you with custom guidance.
            </p>
          </div>

          {status === "success" ? <div className="rounded-xl border border-border bg-card p-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/20 text-primary mb-2">
                <CheckCircle2 className="size-6 text-primary" />
              </div>
              <h3 className="text-2xl font-serif font-semibold text-foreground">
                Estimate request received
              </h3>
              <p className="text-muted-foreground text-sm max-w-md mx-auto">
                Thank you for reaching out. We will review your project requirements and connect with you shortly.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-6">
              {status === "error" && <Alert variant="destructive" className="border border-destructive/30">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Submission issue</AlertTitle>
                  <AlertDescription>
                    {errorMessage}{" "}
                    <span className="block mt-1">
                      Direct phone:{" "}
                      <a href="tel:4708479076" className="underline font-medium">
                        (470) 847-9076
                      </a>{" "}
                      &bull; Direct email:{" "}
                      <a href="mailto:serviceleadalerts@gmail.com" className="underline font-medium">
                        serviceleadalerts@gmail.com
                      </a>
                    </span>
                  </AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="fullName" type="text" required placeholder="e.g. Eleanor Vance" value={name} onChange={e => setName(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="phoneNumber" type="tel" required placeholder="(404) 555-0182" value={phone} onChange={e => setPhone(e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emailAddress">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="emailAddress" type="email" required placeholder="eleanor@example.com" value={email} onChange={e => setEmail(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zipCode">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="zipCode" type="text" required maxLength={5} placeholder="30319" value={zip} onChange={handleZipChange} aria-invalid={Boolean(zipError)} />
                  {zipError && <p className="text-xs text-destructive font-medium mt-1">
                      {zipError}
                    </p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectType">
                  Project Type <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <select id="projectType" required value={projectType} onChange={e => setProjectType(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                    <option value="" disabled>
                      Select project type
                    </option>
                    <option value="Kitchen">Kitchen</option>
                    <option value="Bathroom">Bathroom</option>
                    <option value="Whole-Home">Whole-Home</option>
                    <option value="Home Addition">Home Addition</option>
                    <option value="Basement">Basement</option>
                    <option value="Interior">Interior</option>
                    <option value="Outdoor Living">Outdoor Living</option>
                    <option value="Deck">Deck</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="preferredTiming">Preferred Timing (Optional)</Label>
                  <select id="preferredTiming" value={timing} onChange={e => setTiming(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                    <option value="">Select timeframe</option>
                    <option value="As soon as possible">As soon as possible</option>
                    <option value="1-3 months">1-3 months</option>
                    <option value="3-6 months">3-6 months</option>
                    <option value="6+ months / just exploring">
                      6+ months / just exploring
                    </option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budgetRange">Budget Range (Optional)</Label>
                  <select id="budgetRange" value={budget} onChange={e => setBudget(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                    <option value="">Select budget range</option>
                    <option value="Under $25,000">Under $25,000</option>
                    <option value="$25,000-$75,000">$25,000-$75,000</option>
                    <option value="$75,000-$150,000">$75,000-$150,000</option>
                    <option value="$150,000-$300,000">$150,000-$300,000</option>
                    <option value="$300,000+">$300,000+</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectDetails">Project Details / Message (Optional)</Label>
                <Textarea id="projectDetails" rows={4} placeholder="Tell us about the scope, architectural ideas, or any specific goals for your space." value={message} onChange={e => setMessage(e.target.value)} />
              </div>

              <div className="space-y-2">
                <div className="flex items-start space-x-3 pt-1">
                  <Checkbox id="consent" checked={consent} onCheckedChange={checked => {
                setConsent(Boolean(checked));
                if (checked) setConsentError(false);
              }} />
                  <Label htmlFor="consent" className="text-xs sm:text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                    I agree to be contacted about my project by phone, text, or email.{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                </div>
                {consentError && <p className="text-xs text-destructive font-medium pl-7">
                    Please acknowledge the contact agreement before submitting.
                  </p>}
              </div>

              <div className="pt-2">
                <Button type="submit" disabled={status === "submitting"} className="w-full h-11 text-base font-medium shadow-md transition-all hover:-translate-y-0.5">
                  {status === "submitting" ? <>
                      <Loader2 className="size-4 animate-spin mr-2" />
                      Submitting request...
                    </> : "Submit estimate request"}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}